/**
 * Per-user gaze calibration model: maps expanded gaze features → normalised
 * screen coordinates (0..1) via two ridge regressions (x and y).
 *
 * Fit pipeline: expand features → z-score standardise each column (bias
 * excluded) → ridge fit → one outlier-trim pass (drop the 10% worst residual
 * samples, e.g. frames where the user glanced away or mid-blink slipped
 * through) → refit.
 */
import { expandFeatures } from './gazeFeatures'
import { dot, ridgeFit } from './ridge'

const LAMBDA = 1e-2
const TRIM_FRACTION = 0.1

interface Sample {
  base: number[] // raw features, kept for offline analysis/export
  phi: number[] // expanded, unstandardised
  x: number
  y: number
}

export interface FitStats {
  samples: number
  trimmed: number
  /** RMS residual on the training set, in normalised screen units. */
  rmsResidual: number
}

export class GazeModel {
  private samples: Sample[] = []
  private mean: number[] | null = null
  private std: number[] | null = null
  private wx: number[] | null = null
  private wy: number[] | null = null

  addSample(baseFeatures: number[], targetX: number, targetY: number): void {
    this.samples.push({
      base: baseFeatures,
      phi: expandFeatures(baseFeatures),
      x: targetX,
      y: targetY,
    })
  }

  /** Raw training data for session export — lets an offline tool refit variants. */
  exportSamples(): { base: number[]; x: number; y: number }[] {
    return this.samples.map((s) => ({ base: s.base, x: s.x, y: s.y }))
  }

  get sampleCount(): number {
    return this.samples.length
  }

  get isFitted(): boolean {
    return this.wx !== null && this.wy !== null
  }

  clear(): void {
    this.samples = []
    this.mean = null
    this.std = null
    this.wx = null
    this.wy = null
  }

  private standardise(phi: number[]): number[] {
    const m = this.mean!
    const s = this.std!
    const out = new Array<number>(phi.length)
    out[0] = 1 // bias column untouched
    for (let i = 1; i < phi.length; i++) out[i] = (phi[i] - m[i]) / s[i]
    return out
  }

  private fitOnce(samples: Sample[]): boolean {
    const d = samples[0].phi.length
    const mean = new Array<number>(d).fill(0)
    const std = new Array<number>(d).fill(0)
    for (const s of samples) for (let i = 1; i < d; i++) mean[i] += s.phi[i]
    for (let i = 1; i < d; i++) mean[i] /= samples.length
    for (const s of samples) {
      for (let i = 1; i < d; i++) std[i] += (s.phi[i] - mean[i]) ** 2
    }
    for (let i = 1; i < d; i++) std[i] = Math.max(Math.sqrt(std[i] / samples.length), 1e-6)
    this.mean = mean
    this.std = std

    const x = samples.map((s) => this.standardise(s.phi))
    const wx = ridgeFit(x, samples.map((s) => s.x), LAMBDA)
    const wy = ridgeFit(x, samples.map((s) => s.y), LAMBDA)
    if (!wx || !wy) return false
    this.wx = wx
    this.wy = wy
    return true
  }

  /** Fit on all collected samples. Returns stats, or null if under-determined. */
  fit(): FitStats | null {
    if (this.samples.length < 30) return null
    if (!this.fitOnce(this.samples)) return null

    // Trim outliers and refit.
    const residuals = this.samples.map((s) => {
      const p = this.predictExpanded(s.phi)!
      return Math.hypot(p.x - s.x, p.y - s.y)
    })
    const order = residuals
      .map((r, i) => ({ r, i }))
      .sort((a, b) => a.r - b.r)
    const keep = Math.max(30, Math.floor(order.length * (1 - TRIM_FRACTION)))
    const kept = order.slice(0, keep).map((o) => this.samples[o.i])
    const trimmed = this.samples.length - kept.length
    if (trimmed > 0 && !this.fitOnce(kept)) return null

    const rms = Math.sqrt(
      kept.reduce((acc, s) => {
        const p = this.predictExpanded(s.phi)!
        return acc + (p.x - s.x) ** 2 + (p.y - s.y) ** 2
      }, 0) / kept.length,
    )
    return { samples: this.samples.length, trimmed, rmsResidual: rms }
  }

  private predictExpanded(phi: number[]): { x: number; y: number } | null {
    if (!this.wx || !this.wy) return null
    const z = this.standardise(phi)
    return { x: dot(this.wx, z), y: dot(this.wy, z) }
  }

  /** Predict normalised screen coords from base features. Unclamped. */
  predict(baseFeatures: number[]): { x: number; y: number } | null {
    return this.predictExpanded(expandFeatures(baseFeatures))
  }
}
